import React from 'react';
import { bool, func, number, string } from 'prop-types';
import { Form as FinalForm, FormSpy } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { numberAtLeast, required } from '../../../util/validators';
import { PURCHASE_PROCESS_NAME } from '../../../transactions/transaction';

import {
  Form,
  FieldSelect,
  FieldTextInput,
  InlineTextButton,
  PrimaryButton,
  H3,
  H6,
  NamedLink,
} from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';

import css from './ProductOrderForm.module.css';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { types as sdkTypes } from '../../../util/sdkLoader';
import * as validators from '../../../util/validators';
import { removeProperties } from '../../../util/removeProperties';

// Browsers can't render huge number of select options.
// (stock is shown inside select element)
// Note: input element could allow ordering bigger quantities
const MAX_QUANTITY_FOR_DROPDOWN = 100;
const { Money, UUID } = sdkTypes;

const renderForm = formRenderProps => {
  const {
    // FormRenderProps from final-form
    handleSubmit,
    form: formApi,

    // Custom props passed to the form component
    intl,
    formId,
    currentStock,
    hasMultipleDeliveryMethods,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    price,
    marketplaceName,
    values,
    currentUser,
    bookmarks,
    listing,
    couponCodeArr,
    couponListings,
    marketplaceCurrency,
    setPrice,
    pristine,
    discountedPrice,
    services
  } = formRenderProps;

  const serviceOnly = listing?.attributes?.publicData?.serviceEnabled;
  const priceValue = listing && listing.attributes.price.amount > 0;

  const currentDate = moment();
  const formatcurrentDate = moment().format('YYYY-MM-DD');
  const listingTitle = listing && listing?.attributes?.title;

  const coupounArray = (couponListings, keyValue) => {
    if (couponListings && couponListings.length && typeof keyValue === 'string') {
      const couponArray = couponListings.filter(
        st => st?.attributes?.publicData?.specificListing?.findIndex(st => st.key == keyValue) >= 0
      );
      const customCouponPresent =
        couponArray &&
        couponArray.length &&
        couponArray.filter(
          st =>
            currentDate.isBetween(
              moment(st?.attributes?.publicData?.dateOfStart),
              moment(st?.attributes?.publicData?.dateOfExpire)
            ) ||
            st?.attributes?.publicData?.dateOfExpire == formatcurrentDate ||
            st?.attributes?.publicData?.dateOfStart == formatcurrentDate
        );
      const couponArrayValues =
        customCouponPresent && customCouponPresent.length
          ? customCouponPresent.map(st => st?.attributes?.publicData?.couponCode)
          : [];
      return couponArrayValues;
    }
  };

  const couponArrayValues = coupounArray(couponListings, listingTitle);
  const allCoupons = couponArrayValues?.concat(couponCodeArr);

  const marketingOnly =
    listing && listing?.attributes?.publicData.productListingType == 'marketing-only';

  const handleOnChange = formValues => {
    const {
      quantity,
      deliveryMethod,
      couponCode,
      priceVariable,
      ButtonClicked,
    } = formValues.values;

    const allData = formValues.values;
    const { discountedPrice } = formRenderProps;

    // Extract listing options from form values
    const listingOptionsData = {};
    Object.keys(allData).forEach(key => {
      if (key.startsWith('listingOption_')) {
        const optionTitle = key.replace('listingOption_', '');
        listingOptionsData[optionTitle] = allData[key];
      }
    });

    const propertiesToRemove = ['discountedPrice', 'couponCode', 'quantity', 'deliveryMethod', 'ButtonClicked'];
    const modifiedObject = removeProperties(allData, propertiesToRemove);

    const stockReservationQuantity = Number.parseInt(quantity, 10);
    const isBrowser = typeof window !== 'undefined';
    if (ButtonClicked && ['ADD_CART', 'REMOVE_CART'].includes(ButtonClicked)) {
      return;
    }
    if (services) {
     
      // For services, we don't need quantity but require deliveryMethod
      if (deliveryMethod) {
        const quantity=Number.parseInt(1, 10);
        onFetchTransactionLineItems({
          orderData: {
            stockReservationQuantity:quantity,
            deliveryMethod,
            couponCode,
            priceVariable,
            discountedPrice,
            modifiedObject,
            listingOptions: listingOptionsData,
          },
          listingId,
          isOwnListing,
        });
      }
    }
    if (isBrowser && stockReservationQuantity && deliveryMethod && !fetchLineItemsInProgress) {
      onFetchTransactionLineItems({
        orderData: {
          stockReservationQuantity,
          deliveryMethod,
          couponCode,
          priceVariable,
          discountedPrice,
          modifiedObject,
          listingOptions: listingOptionsData,
        },
        listingId,
        isOwnListing,
      });
    } else if (
      isBrowser &&
      stockReservationQuantity &&
      deliveryMethod &&
      !fetchLineItemsInProgress
    ) {
      onFetchTransactionLineItems({
        orderData: {
          stockReservationQuantity,
          deliveryMethod,
          couponCode,
          priceVariable,
          discountedPrice,
          modifiedObject,
          listingOptions: listingOptionsData,
        },
        listingId,
        isOwnListing,
      });
    }
  };

  const handleFormSubmit = e => {
    const { quantity = "1", deliveryMethod } = values || {};
    if (!quantity || quantity < 1) {
      // Blur event will show validator message
      formApi.blur('quantity');
      formApi.focus('quantity');
    } else if (!deliveryMethod) {
      // Blur event will show validator message
      formApi.blur('deliveryMethod');
      formApi.focus('deliveryMethod');
    } else {
      handleSubmit(e);
    }
  };

  const breakdownData = {};
  const showBreakdown =
    breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;
  const breakdown = showBreakdown ? (
    <div className={css.breakdownWrapper}>
      <H6 as="h3" className={css.bookingBreakdownTitle}>
        <FormattedMessage id="ProductOrderForm.breakdownTitle" />
      </H6>
      <hr className={css.totalDivider} />
      <EstimatedCustomerBreakdownMaybe
        breakdownData={breakdownData}
        lineItems={lineItems}
        currency={price.currency}
        marketplaceName={marketplaceName}
        processName={PURCHASE_PROCESS_NAME}
      />
    </div>
  ) : null;

  const showContactUser = typeof onContactUser === 'function';

  const onClickContactUser = e => {
    e.preventDefault();
    onContactUser();
  };

  const contactSellerLink = (
    <InlineTextButton onClick={onClickContactUser}>
      <FormattedMessage id="ProductOrderForm.finePrintNoStockLinkText" />
    </InlineTextButton>
  );
  const quantityRequiredMsg = intl.formatMessage({ id: 'ProductOrderForm.quantityRequired' });
  const attributesRequiredMsg = intl.formatMessage({ id: 'ProductOrderForm.attributesRequired' });
  const requiredMsg = validators.required(attributesRequiredMsg);

  const hasStock = currentStock && currentStock > 0;

  const selectableStock =
    currentStock > MAX_QUANTITY_FOR_DROPDOWN ? MAX_QUANTITY_FOR_DROPDOWN : currentStock;
  const quantities = hasStock ? [...Array(selectableStock).keys()].map(i => i + 1) : [];
  const hasNoStockLeft = typeof currentStock != null && currentStock === 0;
  const hasOneItemLeft = typeof currentStock != null && currentStock === 1;
  const submitInProgress = fetchLineItemsInProgress;
  const submitDisabled = isOwnListing || !hasStock;
  const bookmarksList = bookmarks || (currentUser && currentUser.attributes?.profile?.publicData?.bookmarks) || [];
  const targetListingId = listingId?.uuid || listingId;
  const priceVariable = listing?.attributes?.publicData?.priceVariable;
  const generalSizesAttribute =
    listing && priceVariable && listing?.attributes?.publicData[priceVariable];
  
  // Get listing options from publicData
  const listingOptions = listing?.attributes?.publicData?.listingOptions || [];
  
  return (
    <Form>
      <FormSpy subscription={{ values: true }} onChange={handleOnChange} />

      {priceVariable && generalSizesAttribute && generalSizesAttribute.length ? (
        <div>
          Prices vary according to {priceVariable}
          <FieldSelect
            id={priceVariable}
            name={priceVariable}
            onChange={e => {
              const value = generalSizesAttribute.find(st => st.key == e);
              const priceValues = value && new Money(value.amount, marketplaceCurrency);
              setPrice(priceValues);

              // formApi.change("discountedPrice", priceValues)
            }}
            validate={requiredMsg}
          >
            <option selected value="">
              Please select the {priceVariable}
            </option>
            {generalSizesAttribute.map(st => (
              <option value={st.key} key={st.key}>
                {st.label} ${st.amount / 100}
              </option>
            ))}
          </FieldSelect>
        </div>
      ) : null}

      {/* Listing Options Dropdowns */}
      {listingOptions && listingOptions.length > 0 && (
        <div className={css.listingOptionsWrapper}>
          {listingOptions.map((option, index) => (
            <div className={css.formItem} key={`${option.title}-${index}`}>
              <FieldSelect
                id={`listingOption_${option.title}`}
                className={css.deliveryField}
                name={`listingOption_${option.title}`}
                label={option.title}
                validate={requiredMsg}
              >
                <option disabled value="">
                  Please select {option.title.toLowerCase()}
                </option>
                {option.values && option.values.map((value, valueIndex) => (
                  <option key={`${value}-${valueIndex}`} value={value}>
                    {value}
                  </option>
                ))}
              </FieldSelect>
            </div>
          ))}
        </div>
      )}

      {marketingOnly ? null : (
       
        !services ? <>
        {hasNoStockLeft ? null : hasOneItemLeft ? (
          <FieldTextInput
            id={`${formId}.quantity`}
            className={css.quantityField}
            name="quantity"
            type="hidden"
            validate={numberAtLeast(quantityRequiredMsg, 1)}
          />
        ) : (
          <FieldSelect
            id={`${formId}.quantity`}
            className={css.quantityField}
            name="quantity"
            disabled={!hasStock}
            label={intl.formatMessage({ id: 'ProductOrderForm.quantityLabel1' })}
            validate={numberAtLeast(quantityRequiredMsg, 1)}
          >
            <option disabled value="">
              {intl.formatMessage({ id: 'ProductOrderForm.selectQuantityOption1' })}
            </option>
            {quantities.map(quantity => (
              <option key={quantity} value={quantity}>
                {intl.formatMessage({ id: 'ProductOrderForm.quantityOption1' }, { quantity })}
              </option>
            ))}
          </FieldSelect>
        )}
      </>:null
      )}

      <div className={css.varientOptionContent}>
        {listing &&
          listing.length &&
          listing?.attributes?.publicData?.attributes?.map(attribute => {
            const allValues = attribute.key;
            const options = listing?.attributes?.publicData[allValues]
              ? listing?.attributes?.publicData[allValues]
              : [];

            const priceVariable = listing?.attributes?.publicData?.priceVariable == allValues;
            if (priceVariable) {
              return null;
            }
            return (
              <div className={css.formItem} key={attribute.key}>
                <FieldSelect
                  id={attribute.key}
                  className={css.deliveryField}
                  name={attribute.key}
                  label={attribute.label}
                  validate={requiredMsg}
                >
                  <option disabled value="">
                    Please select {attribute.label.toLowerCase()}
                  </option>
                  {options.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </FieldSelect>
              </div>
            );
          })}
      </div>
      <FieldSelect
        id={`${formId}.couponCode`}
        className={css.deliveryField}
        name="couponCode"
        label={intl.formatMessage({ id: 'ProductOrderForm.couponLabel' })}
      >
        <option disabled value="">
          Please select coupon
        </option>
        {allCoupons && allCoupons.length ? (
          allCoupons.map(quantity => (
            <option key={quantity} value={quantity}>
              {quantity}
            </option>
          ))
        ) : (
          <option disabled value="">
            No Coupon currently available
          </option>
        )}
      </FieldSelect>

      <NamedLink
        className={css.seeCoupons}
        name="ProfilePage"
        params={{ id: listing?.author?.id?.uuid }}
        to={{ state: { tab: 'coupon2' } }}
      >
        View available coupons for this listing
      </NamedLink>

      {marketingOnly ? null : (
        <>
          {hasNoStockLeft ? null : hasMultipleDeliveryMethods ? (
            <FieldSelect
              id={`${formId}.deliveryMethod`}
              className={css.deliveryField}
              name="deliveryMethod"
              disabled={!hasStock}
              label={intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodLabel' })}
              validate={required(
                intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodRequired' })
              )}
            >
              <option disabled value="">
                {intl.formatMessage({ id: 'ProductOrderForm.selectDeliveryMethodOption' })}
              </option>
              <option value={'pickup'}>
                {intl.formatMessage({ id: 'ProductOrderForm.pickupOption' })}
              </option>
              <option value={'shipping'}>
                {intl.formatMessage({ id: 'ProductOrderForm.shippingOption' })}
              </option>
            </FieldSelect>
          ) : (
            <>
              {!serviceOnly ? (
                <div className={css.deliveryField}>
                  <H3 rootClassName={css.singleDeliveryMethodLabel}>
                    {intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodLabel' })}
                  </H3>
                  <p className={css.singleDeliveryMethodSelected}>
                    {values.deliveryMethod === 'shipping'
                      ? intl.formatMessage({ id: 'ProductOrderForm.shippingOption' })
                      : intl.formatMessage({ id: 'ProductOrderForm.pickupOption' })}
                  </p>
                  <FieldTextInput
                    id={`${formId}.deliveryMethod`}
                    className={css.deliveryField}
                    name="deliveryMethod"
                    type="hidden"
                  />
                </div>
              ) : null}
            </>
          )}
          {breakdown}
          <div className={css.submitButton}>
            {priceValue && (
              <PrimaryButton
                className={css.buyNowButton}
                type="button"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                onClick={() => {
                  formApi.change('ButtonClicked', 'BUY_NOW');
                  formApi.change('discountedPrice', discountedPrice);
                  handleFormSubmit(values);
                }}
              >
                {hasStock ? (
                  <FormattedMessage id="ProductOrderForm.ctaButton" />
                ) : (
                  <FormattedMessage id="ProductOrderForm.ctaButtonNoStock" />
                )}
              </PrimaryButton>
            )}
            {hasStock && !isOwnListing ? (
              <div className={css.addToCartBtns}>
                <PrimaryButton
                  type="button"
                  className={classNames(
                    bookmarksList && bookmarksList.findIndex(e => e.id === targetListingId) > -1
                      ? css.filled
                      : css.removedCard,
                    css.removeCard
                  )}
                  onClick={e => {
                    formApi.change('ButtonClicked', 'REMOVE_CART');
                    handleFormSubmit(values);
                  }}
                >
                  Remove from Cart
                </PrimaryButton>

                <PrimaryButton
                  type="button"
                  className={classNames(
                    bookmarksList && bookmarksList.findIndex(e => e.id === targetListingId) > -1
                      ? null
                      : css.bookmark
                  )}
                  onClick={e => {
                    formApi.change('ButtonClicked', 'ADD_CART');
                    formApi.change('discountedPrice', discountedPrice);
                    handleFormSubmit(values);
                  }}
                  disabled={submitDisabled}
                >
                  Add to Cart
                </PrimaryButton>
              </div>
            ) : null}
          </div>
        </>
      )}
    </Form>
  );
};

const ProductOrderForm = props => {
  const intl = useIntl();
  const { price, currentStock, pickupEnabled, shippingEnabled } = props;

  const hasOneItemLeft = currentStock && currentStock === 1;
  const quantityMaybe = hasOneItemLeft ? { quantity: '1' } : {};
  const singleDeliveryMethodAvailableMaybe =
    shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : !shippingEnabled && pickupEnabled
      ? { deliveryMethod: 'pickup' }
      :  !shippingEnabled && !pickupEnabled?{
          deliveryMethod: 'pickup' }
      :{};
  const hasMultipleDeliveryMethods = pickupEnabled && shippingEnabled;
  const initialValues = { ...quantityMaybe, ...singleDeliveryMethodAvailableMaybe };

  return (
    <FinalForm
      initialValues={initialValues}
      hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
      {...props}
      intl={intl}
      render={renderForm}
    />
  );
};

ProductOrderForm.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  currentStock: null,
  listingId: null,
  isOwnListing: false,
  lineItems: null,
  fetchLineItemsError: null,
};

ProductOrderForm.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,

  // form
  formId: string.isRequired,
  onSubmit: func.isRequired,

  // listing
  listingId: propTypes.uuid,
  price: propTypes.money,
  currentStock: number,
  isOwnListing: bool,

  // line items
  lineItems: propTypes.lineItems,
  onFetchTransactionLineItems: func.isRequired,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // other
  onContactUser: func,
};

export default ProductOrderForm;
